import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BedDouble, Car, Check, Flame, Footprints, Music, Tent, X } from "lucide-react";
import { Reveal } from "../components/Reveal";

const WA = "https://wa.me/33623502131?text=Bonjour%20Julie%2C%20je%20suis%20int%C3%A9ress%C3%A9e%20par%20le%20trek%20au%20Sahara%20(7-14%20d%C3%A9c.%202026)%2C%20peux-tu%20m%27envoyer%20les%20infos%20%3F";

const PROGRAMME = [
  { icon: BedDouble, title: "Nuit à Marrakech", text: "Arrivée et nuit dans un riad, avec le petit-déjeuner." },
  { icon: Car, title: "Voiturier jusqu'au désert", text: "Un service de voiturier te récupère et t'emmène aux portes du désert." },
  { icon: Footprints, title: "6 jours de marche", text: "5 nuits et 6 jours avec les nomades, 3 à 4 h de marche par jour dans les dunes. Fatiguée ? Tu peux louer un dromadaire." },
  { icon: Flame, title: "Repas au feu", text: "Les repas sont préparés par nos hôtes, à la lumière du feu et sous les étoiles." },
  { icon: Tent, title: "Nuits berbères", text: "Nuitées sous les tentes berbères — prévois ton sac de couchage, en décembre les nuits sont fraîches." },
  { icon: Music, title: "On se libère", text: "Tambours, constellations sous les étoiles, méditation, chant, danse : au réveil comme au coucher, on respire et on lâche." },
];

const INCLUS = ["Nuit en riad à Marrakech avec petit-déjeuner", "Transport jusqu'aux portes du désert", "5 nuits sous tentes berbères", "Tous les repas préparés par nos hôtes", "La caravane et l'accompagnement des nomades", "Tous les ateliers : breathwork, constellations, méditation"];
const NON_INCLUS = ["Ton vol (tu le choisis selon ta ville de départ)", "Le 1er repas", "Ton sac de couchage", "La location d'un dromadaire si besoin"];

const FAQ = [
  { q: "Et si je ne suis pas sportive ?", a: "Le rythme est doux : 3 à 4 h de marche par jour, aux pas de la caravane. Et si tu es fatiguée, tu peux louer un dromadaire pour porter tes pas." },
  { q: "Il fait froid en décembre ?", a: "Les journées sont douces et ensoleillées, mais les nuits sont fraîches dans le désert — prévois un bon sac de couchage, le reste est fourni." },
  { q: "Comment je réserve ?", a: "Un simple acompte suffit, et le paiement en plusieurs fois est possible. Écris-moi sur WhatsApp ou via le formulaire, je t'envoie tout." },
];

const GALERIE = [
  { src: "/images/trek-coucher.jpg", alt: "Coucher de soleil sur les dunes du Sahara" },
  { src: "/images/trek-feu.jpg", alt: "Veillée autour du feu au bivouac" },
  { src: "/images/trek-the.jpg", alt: "Mohammed préparant le thé dans le désert" },
  { src: "/images/trek-groupe.jpg", alt: "Le groupe au bivouac avec les dromadaires" },
];

export default function LandingTrek() {
  const [videoError, setVideoError] = useState(false);

  return (
    <div data-testid="landing-trek" className="bg-olive-dark text-beige-100 min-h-screen">
      {/* MINI BARRE */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-olive-dark/80 border-b border-beige-100/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand" className="flex items-center gap-2">
            <img src="/images/logo-icon.png" alt="" className="h-7 w-7 object-contain invert" />
            <span className="font-serif text-lg">Julie Zittel</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-block rounded-full bg-gold px-3 py-1 text-xs font-medium text-olive-dark" data-testid="landing-places-top">Plus que 3 places</span>
            <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-top" className="inline-flex items-center rounded-full bg-[#25D366] px-4 py-2 text-xs sm:text-sm font-medium text-white transition-transform duration-300 hover:scale-105">
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* HERO VIDÉO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        {!videoError ? (
          <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline poster="/images/trek-dunes.jpg" onError={() => setVideoError(true)} data-testid="landing-video">
            <source src="/api/videos/trek-sahara.webm" type="video/webm" />
            <source src="/api/videos/trek-sahara.mp4" type="video/mp4" />
          </video>
        ) : (
          <img src="/images/trek-dunes.jpg" alt="Dunes du Sahara au coucher du soleil" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-olive-dark via-olive-dark/40 to-olive-dark/20" />
        <div className="relative w-full max-w-6xl mx-auto px-6 pb-20 pt-40">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Trek initiatique · Maroc · 7-14 décembre 2026</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] max-w-3xl">
              8 jours dans une vie, pour te libérer dans le désert
            </h1>
            <p className="mt-6 max-w-xl text-beige-200/90 leading-relaxed">
              Marche avec les nomades, nuits sous les étoiles, tambours et constellations au cœur des dunes du Sahara. Tout est organisé — il ne te reste qu'à choisir ton vol.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-hero" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_15px_35px_-10px_rgba(37,211,102,0.6)]">
                Je réserve sur WhatsApp
              </a>
              <span className="font-serif text-2xl">960 € <span className="text-sm text-beige-300/80 font-sans">tout compris</span></span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* IMAGINE */}
      <section className="py-24 px-6 max-w-4xl mx-auto text-center" data-testid="landing-imagine">
        <Reveal>
          <p className="font-serif text-2xl sm:text-3xl font-light italic leading-relaxed text-beige-100">
            « Imagine… exporte-toi un instant dans le silence du désert, ferme les yeux. Ressens-tu la chaleur du soleil de décembre sur ton visage ? Le sable sous tes pas ? L'immensité des dunes à perte de vue ? »
          </p>
          <p className="mt-8 text-beige-300/85 leading-relaxed max-w-2xl mx-auto">
            Un instant suspendu au milieu du tumulte de ta vie. 8 jours pour te recentrer, respirer, te libérer — prendre le temps hors du temps.
          </p>
        </Reveal>
      </section>

      {/* PROGRAMME */}
      <section className="pb-24 px-6 max-w-6xl mx-auto" data-testid="landing-programme">
        <Reveal className="mb-14 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Le déroulement</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light">Tout est prévu, jour après jour</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {PROGRAMME.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.1}>
              <div className="border-t-2 border-gold/50 pt-6" data-testid={`landing-etape-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-beige-100/10 border border-beige-100/20">
                  <p.icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-serif text-2xl">{p.title}</h3>
                <p className="mt-3 text-sm text-beige-300/85 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALERIE */}
      <section className="pb-24 px-6 max-w-6xl mx-auto" data-testid="landing-galerie">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {GALERIE.map((g, i) => (
            <Reveal key={g.src} delay={i * 0.08}>
              <div className={`group overflow-hidden rounded-2xl border border-beige-100/15 ${i % 2 ? "lg:mt-10" : ""}`}>
                <img src={g.src} alt={g.alt} loading="lazy" className="w-full h-64 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-10 max-w-md mx-auto overflow-hidden rounded-2xl border border-beige-100/15" data-testid="landing-affiche">
            <img src="/images/trek-affiche.jpg" alt="Affiche du programme du trek initiatique au Sahara" loading="lazy" className="w-full h-auto" />
          </div>
        </Reveal>
      </section>

      {/* INCLUS / NON INCLUS + PRIX */}
      <section className="pb-24 px-6 max-w-6xl mx-auto" data-testid="landing-prix">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="h-full bg-beige-100/5 border border-beige-100/15 rounded-3xl p-8 sm:p-10">
              <h2 className="font-serif text-3xl font-light mb-6">Ce qui est inclus</h2>
              <ul className="space-y-3">
                {INCLUS.map((x) => (
                  <li key={x} className="flex items-start gap-3 text-sm text-beige-200/90">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-gold" strokeWidth={2} /> {x}
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-5 border-t border-beige-100/10 text-xs text-beige-300/70">Non inclus : {NON_INCLUS.join(" · ").toLowerCase()}.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full bg-beige-100 text-olive-dark rounded-3xl p-8 sm:p-10 flex flex-col">
              <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4">Tarif tout compris</p>
              <p className="font-serif text-6xl sm:text-7xl font-light">960 €</p>
              <p className="mt-4 text-sage leading-relaxed">8 jours — repas, couchage et ateliers inclus. Acompte de réservation, paiement en plusieurs fois possible.</p>
              <p className="mt-4 inline-flex w-fit items-center rounded-full bg-terracotta/15 border border-terracotta/40 px-4 py-2 text-sm font-medium text-terracotta" data-testid="landing-places">Plus que 3 places disponibles</p>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-prix" className="mt-auto pt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5">
                Je réserve ma place <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24 px-6 max-w-4xl mx-auto" data-testid="landing-faq">
        <Reveal className="mb-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl font-light">Les questions qu'on me pose</h2>
        </Reveal>
        <div className="space-y-5">
          {FAQ.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.08}>
              <div className="bg-beige-100/5 border border-beige-100/15 rounded-2xl p-7" data-testid={`landing-faq-${i}`}>
                <h3 className="font-serif text-xl text-gold">{f.q}</h3>
                <p className="mt-3 text-sm text-beige-300/85 leading-relaxed">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-beige-100/10" data-testid="landing-cta-final">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">Alors, tu tentes l'aventure avec Julie, Mohammed et sa famille ?</h2>
            <p className="mt-6 text-beige-300/85">Du 7 au 14 décembre 2026 · 960 € tout compris · plus que 3 places</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp-bottom" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5">
                Je réserve sur WhatsApp
              </a>
              <a href="mailto:juliezittel6@gmail.com" data-testid="landing-email" className="inline-flex items-center rounded-full border border-beige-100/40 px-8 py-4 text-sm text-beige-100 transition-all duration-300 hover:bg-beige-100/10">
                juliezittel6@gmail.com
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-beige-100/10 py-6 text-center text-xs text-beige-300/50">
        <Link to="/" className="hover:text-beige-200 transition-colors">juliezittel.com</Link> — Thérapeute & formatrice, Bayonne · Bellignat
      </footer>
    </div>
  );
}
